import {
  ctaVariants,
  HeaderTextBlock,
  ImageFlyoutBlock,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { getLinkIcon, useAnalytics } from '@greystar/lib';
import { ImageWithContentBlocksProps, ResponsiveImage } from '@greystar/ui';
import {
  Text as JssText,
  Placeholder,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

/**
 * @param props - The properties for the ImageAndTextBlock component.
 * @param props.fields - The fields provided by the datasource.
 * @param props.fields.heading - The main heading text.
 * @param props.fields.subHeading - The sub heading text.
 * @param props.fields.text - The text to displau.
 * @param props.fields.image - The image to display.
 * @param props.fields.cta - The call-to-action link.
 *
 * @returns A JSX element rendering the ImageAndTextBlock.
 */
export const ImageWithContentBlocks = (props: ImageWithContentBlocksProps): JSX.Element => {
  const { heading, subHeading, highlightText, text, image, cta } = props.fields;
  const analytics = useAnalytics();

  const handleClick = () => {
    const eventData = {
      event: 'rent_with_us',
      action_type: 'entry',
      module_name: heading.value,
      inpage_location: 'middle',
      click_text: cta?.value?.text ?? 'CTA',
      click_type: 'internal',
      destination_url: cta?.value?.href ?? '/',
    };
    analytics.trackEvent('rent_with_us', eventData);
  };

  const JssFields = {
    headerHeading: <JssText field={heading} />,
    headerSubtitle: <JssText field={subHeading} />,
    flyoutTitle: <JssText field={highlightText} />,
    flyoutDescription: <JssText field={text} />,
  };

  const propsObj = {
    header: JssFields.headerHeading,
    subTitle: JssFields.headerSubtitle,
    flyout: {
      title: JssFields.flyoutTitle,
      description: JssFields.flyoutDescription,
    },
    ctaLink: {
      label: cta?.value?.text ?? 'CTA',
      href: cta?.value?.href ?? '/',
      variant: ctaVariants.secondary,
      icon: getLinkIcon(),
    },
    image: <ResponsiveImage image={image} fill={true} />,
    onClick: handleClick,
  };
  const bottomColour = props?.params?.bottomColour ?? null;
  const pushUpPageSection = props?.params?.pushUpPageSection == '1';
  const borderPosition = props?.params?.borderPosition ?? undefined;

  return (
    <PageSection
      isFlushed
      borderPosition={borderPosition}
      pushUpPageSection={pushUpPageSection ?? false}
    >
      <PageSection as="div" borderPosition={borderPosition}>
        <PageSectionContent>
          <HeaderTextBlock direction="reverse">
            <HeaderTextBlock.Subtitle variant="secondary" subtitle={propsObj.subTitle} />
            <HeaderTextBlock.Header
              header={propsObj.header}
              as={props.params?.headingStyles ?? undefined}
            />
          </HeaderTextBlock>
        </PageSectionContent>
      </PageSection>
      <PageSection as="div" isSplitInverted bottomColour={bottomColour} isRightFlushed isTopFlushed>
        <PageSectionContent>
          <ImageFlyoutBlock
            flyout={propsObj.flyout}
            ctaLink={propsObj.ctaLink}
            image={propsObj.image}
            onClick={propsObj.onClick}
          />
        </PageSectionContent>
      </PageSection>
      <Placeholder
        name={`image-content-blocks-${props.params.DynamicPlaceholderId}`}
        rendering={props.rendering}
      />
    </PageSection>
  );
};

export default withDatasourceCheck()<ImageWithContentBlocksProps>(ImageWithContentBlocks);
